import React, { createContext, useContext, useMemo } from 'react';
import { DateTime } from 'luxon';
import { useLabels } from 'modules/common/hooks/useLabels';
import { AnalyticsMetadataType, LabelType } from 'graphql/types';
import { useGetAnalyticsMetaData } from 'modules/Insights/Notes/hooks/useGetAnalyticsMetaData';

export type AnalyticsMetaDataOEF = {
  id: string;
  name: string;
};

type OEFChartContextValue = {
  oefLabels: AnalyticsMetaDataOEF[];
  savedOefLabels: AnalyticsMetaDataOEF[];
  hasMore: boolean;
  isLoading: boolean;
  isEmpty: boolean;
};

export const OEFChartContext = createContext<OEFChartContextValue | null>(null);

export const useOEFChartContext = (): OEFChartContextValue => {
  const value = useContext(OEFChartContext);
  if (!value) {
    throw Error('should be called in OEFChartContextProvider scope');
  }
  return value;
};

export type OEFChartContextProviderType = {
  children?: React.ReactNode;
  insightContext: () => any;
  AnalyticsMetadataInput: AnalyticsMetadataType;
};

const OEFChartContextProvider: React.FC<OEFChartContextProviderType> = ({
  children,
  insightContext,
  AnalyticsMetadataInput
}: OEFChartContextProviderType) => {
  const { data: savedOefList } = useGetAnalyticsMetaData(
    AnalyticsMetadataInput
  );

  const { currentPeriod } = insightContext();
  const { endDate } = currentPeriod ?? {};
  const { loading, timesheetLabels } = useLabels(
    endDate ? DateTime.fromISO(endDate) : DateTime.local()
  );

  const savedOefLabels = useMemo(
    () =>
      savedOefList
        ?.map(
          (x) =>
            ({
              id: x?.id,
              name: x?.name
            } as AnalyticsMetaDataOEF)
        )
        .filter((x) => timesheetLabels.map((y) => y.id).includes(x.id)),
    [savedOefList, timesheetLabels]
  );

  const oefLabels = useMemo(
    () =>
      timesheetLabels
        ?.filter((i) => i.type === LabelType.Select)
        .map((x) => ({ id: x.id, name: x.name })),
    [timesheetLabels]
  );

  const value = useMemo(
    () => ({
      oefLabels: oefLabels,
      savedOefLabels: savedOefLabels,
      hasMore: savedOefLabels.length < oefLabels.length,
      isLoading: loading,
      isEmpty: oefLabels.length === 0
    }),
    [loading, oefLabels, savedOefLabels]
  );
  return (
    <OEFChartContext.Provider value={value}>
      {children}
    </OEFChartContext.Provider>
  );
};

export default OEFChartContextProvider;
